import type { ListItemProps } from "@/components/ListItem/ListItem";
import MultiSectionDetailsCard from "@/components/MultiSectionDetailsCard";
import { toUTCFormat, toFormat, isSameDay } from "@/lib/dateHelpers";
import type {
  ParticipationActivityDetails,
  ParticipationDetailData,
  ParticipationDetailContentProps,
  ParticipationLegacyActivityDetails
} from "../types";
import { PersonObjectHeading } from "@/features/person/components/PersonObjectHeading";
import SectionHeading from "@/components/SectionHeading";
import Stepper from "@/components/Stepper";
import type { StepType } from "@/components/Stepper/Stepper";
import { renderContent } from "@/lib/htmlContentHelpers";
import ManagingSectionLocationCard from "@/pages/activities/_components/ManagingSectionLocationCard";

function formatDateRange(startDate?: string, endDate?: string): string | undefined {
  if (!startDate) {
    return undefined;
  }

  if (!endDate || isSameDay(startDate, endDate)) {
    return toUTCFormat(startDate) ?? undefined;
  }

  return `${toUTCFormat(startDate)} - ${toUTCFormat(endDate)}`;
}

function buildParticipationDetails(participation: ParticipationDetailData): ListItemProps[] {
  const requirementType = participation.creditType;
  const participationDates = formatDateRange(participation.startDate, participation.endDate);
  const showCreditsRequested =
    participation.creditsAppliedFor != null &&
    participation.creditsAwarded != null &&
    participation.creditsAppliedFor !== participation.creditsAwarded;

  const items: ListItemProps[] = [
    ...(participation.creditsAwarded != null
      ? [
          {
            iconName: "stars" as const,
            iconBackground: true,
            overline: "Credits Awarded",
            headline: `${participation.creditsAwarded} ${requirementType ?? ""}`.trim()
          } as ListItemProps
        ]
      : []),
    ...(showCreditsRequested
      ? [
          {
            iconName: "grade" as const,
            iconBackground: true,
            overline: "Credits Requested",
            headline: `${participation.creditsAppliedFor} ${requirementType ?? ""}`.trim()
          } as ListItemProps
        ]
      : []),
    ...(participationDates
      ? [
          {
            iconName: "event" as const,
            iconBackground: true,
            overline: "Dates",
            headline: participationDates
          } as ListItemProps
        ]
      : []),
    ...(participation.quantity
      ? [
          {
            iconName: "pin" as const,
            iconBackground: true,
            overline: "Quantity",
            headline: participation.quantity
          } as ListItemProps
        ]
      : []),
    ...(participation.attendanceType
      ? [
          {
            iconName: "groups" as const,
            iconBackground: true,
            overline: "Attendance Type",
            headline: participation.attendanceType
          } as ListItemProps
        ]
      : []),
    ...(participation.participationRole
      ? [
          {
            iconName: "badge" as const,
            iconBackground: true,
            overline: "Role",
            headline: participation.participationRole
          } as ListItemProps
        ]
      : []),
    ...(participation.creditRate
      ? [
          {
            iconName: "percent" as const,
            iconBackground: true,
            overline: "Rate",
            headline: participation.creditRate
          } as ListItemProps
        ]
      : [])
  ];

  return items;
}

function buildLegacyActivityDetails(legacyActivityDetails?: ParticipationLegacyActivityDetails): ListItemProps[] {
  if (!legacyActivityDetails) {
    return [];
  }

  const items: ListItemProps[] = [
    ...(legacyActivityDetails.activityTitle
      ? [
          {
            iconName: "description" as const,
            iconBackground: true,
            overline: "Title",
            headline: legacyActivityDetails.activityTitle
          } as ListItemProps
        ]
      : []),
    ...(legacyActivityDetails.activityType
      ? [
          {
            iconName: "subject" as const,
            iconBackground: true,
            overline: "Activity Type",
            headline: legacyActivityDetails.activityType
          } as ListItemProps
        ]
      : [])
  ];

  return items;
}

function buildActivityDetails(activityDetails?: ParticipationActivityDetails): ListItemProps[] {
  if (!activityDetails) {
    return [];
  }

  const activityDates = formatDateRange(activityDetails.startDate, activityDetails.endDate);

  const items: ListItemProps[] = [
    ...(activityDetails.title
      ? [
          {
            iconName: "description" as const,
            iconBackground: true,
            overline: "Activity Title",
            headline: activityDetails.title
          } as ListItemProps
        ]
      : []),
    ...(activityDetails.activityType
      ? [
          {
            iconName: "subject" as const,
            iconBackground: true,
            overline: "Activity Type",
            headline: activityDetails.activityType
          } as ListItemProps
        ]
      : []),
    ...(activityDetails.series
      ? [
          {
            iconName: "collections_bookmark" as const,
            iconBackground: true,
            overline: "Series",
            headline: activityDetails.series
          } as ListItemProps
        ]
      : []),
    ...(activityDates
      ? [
          {
            iconName: "event" as const,
            iconBackground: true,
            overline: "Dates",
            headline: activityDates
          } as ListItemProps
        ]
      : []),
    ...(activityDetails.duration
      ? [
          {
            iconName: "schedule" as const,
            iconBackground: true,
            overline: "Duration",
            headline: activityDetails.duration
          } as ListItemProps
        ]
      : [])
  ];

  return items;
}

function buildActivityHistoryItems(participation: ParticipationDetailData): StepType[] {
  return [
    {
      headline: (
        <>
          {participation?.author || "MYPGA"} submitted participation in{" "}
          <span className="text-sys-brand-primary">{participation?.title}</span> successfully
        </>
      ),
      supportingText: toFormat(participation?.submittedAt || new Date().toISOString(), "ccc LLL dd, yyyy h:mm a") || ""
    }
  ];
}

export default function ParticipationDetailContent({ pgaId, participation }: ParticipationDetailContentProps) {
  const participationDetails = buildParticipationDetails(participation);
  const legacyActivityDetails = buildLegacyActivityDetails(participation.legacyActivityDetails);
  const activityDetails = buildActivityDetails(participation.activityDetails);
  const stepsList = buildActivityHistoryItems(participation);

  return (
    <>
      <PersonObjectHeading pgaId={pgaId} hideActions disableSticky />
      <div className="flex flex-col">
        <div className="grid grid-cols-1 md:grid-cols-12 sm:gap-12">
          <div className="col-span-1 md:col-span-4 mt-12 flex flex-col gap-6">
            <div className="mt-6 flex flex-col gap-6">
              {participationDetails.length > 0 && (
                <MultiSectionDetailsCard
                  sections={[{ heading: "Participation Details", options: participationDetails }]}
                />
              )}
              {legacyActivityDetails.length > 0 && (
                <MultiSectionDetailsCard
                  sections={[{ heading: "Activity Details (Legacy)", options: legacyActivityDetails }]}
                />
              )}
              {activityDetails.length > 0 && (
                <MultiSectionDetailsCard sections={[{ heading: "Activity Details", options: activityDetails }]} />
              )}
              {participation.managingSection?.name || participation.location ? (
                <ManagingSectionLocationCard
                  sectionName={participation.managingSection?.name}
                  location={participation.location}
                />
              ) : null}
            </div>
          </div>
          <div className="col-span-1 md:col-span-8 sm:mt-7">
            <div className="border-0 border-sys-brand-secondary-container pb-12">
              <SectionHeading heading="Description" />
              <div className="text-body-large word-break text-sys-brand-on-surface">
                {renderContent(
                  participation.description || participation.creditDescription2 || "No description provided",
                  { enhancedCleanup: false }
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <SectionHeading heading="Activity History" />
      <div className="[&_*]:!cursor-default">
        <Stepper variant="outlined" onStepClick={() => {}} stepsList={stepsList} />
      </div>
    </>
  );
}
